import type { FormStepper } from '@paid-ui/types/form';

import { forwardRef } from 'react';
import { useRouter } from 'next/router';
import { AlertTriangleIcon } from 'lucide-react';
import { useFormContext } from 'react-hook-form';
import { twMerge } from 'tailwind-merge';
import { useSnapshot } from 'valtio/react';

import { Button } from '@paid-ui/components/button';
import { ArrowLeftIcon } from '@paid-ui/icons/arrow-left';

import { signUpManager } from '../../models';
import { OwnerType, SignUpStep } from '../../models/enums';

export interface StepFormHeaderProps<T extends number = number>
  extends React.HTMLAttributes<HTMLDivElement> {
  steps: FormStepper<T>[];
  currentStep: T;
  disabled?: boolean;
  onStepChange?: (step: T) => void;
}

const StepFormHeader = forwardRef(
  <T extends number = number>(
    {
      steps,
      currentStep,
      disabled,
      onStepChange,
      hidden,
      className,
      children,
      ...restProps
    }: StepFormHeaderProps<T>,
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const form = useFormContext();
    const router = useRouter();
    const { invitedSignUp, partyType } = useSnapshot(signUpManager);

    if (hidden) {
      return null;
    }

    const currentStepper = steps.find((step) => step.index === currentStep);

    if (!currentStepper) {
      return null;
    }

    const { index, title: defaultTitle, description } = currentStepper;

    let title = defaultTitle;
    let dom = description ? <p className="text-sm text-grey-dark">{description}</p> : null;

    const hideGoBack = invitedSignUp
      ? [
          SignUpStep.TYPE_AND_EMAIL,
          SignUpStep.OWNER_DETAILS,
          SignUpStep.OWNER_TYPE,
          SignUpStep.OWNER_CONFIRM,
          SignUpStep.SIGN_CONTRACT,
          SignUpStep.COMPLETE_CONTRACT,
          SignUpStep.REVIEW_CONTRACT,
          SignUpStep.CREATE_CONTRACT,
        ].includes(index)
      : [
          SignUpStep.TYPE_AND_EMAIL,
          SignUpStep.OWNER_CONFIRM,
          SignUpStep.SIGN_CONTRACT,
          SignUpStep.COMPLETE_CONTRACT,
          SignUpStep.REVIEW_CONTRACT,
          SignUpStep.CREATE_CONTRACT,
        ].includes(index);

    if ([SignUpStep.OWNER_DETAILS, SignUpStep.OWNER_TYPE].includes(index) && partyType) {
      dom = <p className="text-sm text-grey-dark">Select how you would like to proceed.</p>;
      switch (partyType) {
        case OwnerType.INDIVIDUALS: {
          title = "You've been invited to join Paid as an individual.";
          break;
        }
        case OwnerType.BUSINESS: {
          title = "You've been invited to join Paid as a business.";
          break;
        }
        default:
      }
    }

    if (index === SignUpStep.OWNER_CONFIRM) {
      dom = (
        <p className="text-sm text-grey-dark">
          You've been invited to join Paid as a business. Switching to join as an individual sets
          how you're represented on contracts and{' '}
          <span className="font-medium text-pink">can't be changed later</span>.
        </p>
      );
    }

    if (index === SignUpStep.ONE_TIME_CODE) {
      const email = form.watch('email');
      dom =
        description && email ? (
          <p className="text-sm text-grey-dark">
            {description}
            <br />
            <span className="font-medium">{email}</span>
          </p>
        ) : null;
    }

    return (
      <header ref={ref} className={twMerge('shrink-0', className)} {...restProps}>
        <div className="space-y-3 text-center">
          {hideGoBack ? null : (
            <Button size="xs" variant="subtle" full={false} onClick={() => router.back()}>
              <ArrowLeftIcon />
              <span>Go back</span>
            </Button>
          )}
          {index === SignUpStep.OWNER_CONFIRM ? (
            <AlertTriangleIcon className="mx-auto h-6 w-6 text-pink" />
          ) : null}
          <h2 className="text-xl font-semibold">{title}</h2>
          {dom}
        </div>
        {children}
      </header>
    );
  },
);

StepFormHeader.displayName = 'StepFormHeader';

export default StepFormHeader as <T extends number = number>(
  props: StepFormHeaderProps<T> & { ref?: React.Ref<HTMLDivElement> },
) => JSX.Element;
